import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, X } from 'lucide-react';
import { useChannels, useCreatePost, useUpdatePost, useSocialPost } from '@/hooks/useSocial';
import { Card, Button, Input, Textarea } from '@solid/ui';
import type { SocialNetwork, PubChannel } from '@/types';

const NETWORK_LABELS: Record<SocialNetwork, string> = {
  FACEBOOK: 'Facebook',
  INSTAGRAM: 'Instagram',
  TWITTER: 'X / Twitter',
  LINKEDIN: 'LinkedIn',
  PINTEREST: 'Pinterest',
  TIKTOK: 'TikTok',
  YOUTUBE: 'YouTube',
  GOOGLE_BUSINESS: 'Google Business',
  TELEGRAM: 'Telegram',
  WORDPRESS: 'WordPress',
  MASTODON: 'Mastodon',
  THREADS: 'Threads',
  BLUESKY: 'Bluesky',
};

export default function SocialPostEditor() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  const { data: channels = [], isLoading: channelsLoading } = useChannels();
  const { data: existingPost } = useSocialPost(id);
  const createMutation = useCreatePost();
  const updateMutation = useUpdatePost();

  const [defaultContent, setDefaultContent] = useState('');
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [showOverrides, setShowOverrides] = useState(false);
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [mediaUrls, setMediaUrls] = useState<string[]>([]);
  const [newMediaUrl, setNewMediaUrl] = useState('');

  // Load existing post data
  useEffect(() => {
    if (existingPost) {
      setDefaultContent(existingPost.content.default || '');
      setOverrides(existingPost.content.overrides || {});
      setShowOverrides(
        Object.keys(existingPost.content.overrides || {}).length > 0,
      );
      setSelectedChannels(
        existingPost.postChannels.map((pc) => pc.channelId),
      );
      setMediaUrls(existingPost.mediaUrls || []);
    }
  }, [existingPost]);

  const activeChannels = channels.filter((ch) => ch.isActive);

  const toggleChannel = (channelId: string) => {
    setSelectedChannels((prev) =>
      prev.includes(channelId)
        ? prev.filter((id) => id !== channelId)
        : [...prev, channelId],
    );
  };

  const addMediaUrl = () => {
    const url = newMediaUrl.trim();
    if (url && !mediaUrls.includes(url)) {
      setMediaUrls((prev) => [...prev, url]);
      setNewMediaUrl('');
    }
  };

  const removeMediaUrl = (index: number) => {
    setMediaUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!defaultContent.trim() || selectedChannels.length === 0) return;

    const contentPayload: any = { default: defaultContent.trim() };
    const activeOverrides: Record<string, string> = {};
    for (const [key, val] of Object.entries(overrides)) {
      if (val.trim()) activeOverrides[key] = val.trim();
    }
    if (Object.keys(activeOverrides).length > 0) {
      contentPayload.overrides = activeOverrides;
    }

    if (isEditing) {
      await updateMutation.mutateAsync({
        id,
        data: {
          content: contentPayload,
          channelIds: selectedChannels,
          mediaUrls: mediaUrls.length > 0 ? mediaUrls : [],
        },
      });
    } else {
      await createMutation.mutateAsync({
        content: contentPayload,
        channelIds: selectedChannels,
        mediaUrls: mediaUrls.length > 0 ? mediaUrls : undefined,
      });
    }

    navigate('/admin/social');
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  // Get unique networks from selected channels for override options
  const selectedNetworks = new Set(
    activeChannels
      .filter((ch) => selectedChannels.includes(ch.id))
      .map((ch) => ch.network),
  );

  return (
    <div className="page-enter space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/social')}
          className="p-2 rounded-lg hover:bg-grey-100 transition-colors"
          aria-label="Back to social posts"
        >
          <ArrowLeft className="w-5 h-5 text-grey-500" />
        </button>
        <h1 className="text-heading-2 text-secondary">
          {isEditing ? 'Edit Post' : 'New Post'}
        </h1>
      </div>

      {/* Default content */}
      <Card padding="md">
        <h3 className="text-heading-4 text-secondary mb-4">Content</h3>
        <Textarea
          label="Post Content"
          placeholder="What would you like to share?"
          rows={5}
          value={defaultContent}
          onChange={(e) => setDefaultContent(e.target.value)}
        />

        {/* Per-platform overrides */}
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowOverrides(!showOverrides)}
            className="text-sm text-primary hover:text-primary-700 font-medium"
          >
            {showOverrides
              ? 'Hide platform-specific content'
              : 'Customize per platform'}
          </button>

          {showOverrides && (
            <div className="mt-3 space-y-3">
              {Array.from(selectedNetworks).map((network) => (
                <Textarea
                  key={network}
                  label={NETWORK_LABELS[network]}
                  placeholder={`Custom content for ${NETWORK_LABELS[network]} (optional)`}
                  rows={3}
                  value={overrides[network] || ''}
                  onChange={(e) =>
                    setOverrides((prev) => ({
                      ...prev,
                      [network]: e.target.value,
                    }))
                  }
                />
              ))}
              {selectedNetworks.size === 0 && (
                <p className="text-sm text-grey-400">
                  Select channels below to customize per platform
                </p>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Channel selection */}
      <Card padding="md">
        <h3 className="text-heading-4 text-secondary mb-4">Channels</h3>
        {channelsLoading ? (
          <p className="text-grey-400 text-sm">Loading channels...</p>
        ) : activeChannels.length === 0 ? (
          <p className="text-grey-400 text-sm">
            No channels synced. Go to Settings &rarr; Social Channels to sync
            from Publer.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {activeChannels.map((channel) => (
              <ChannelCheckbox
                key={channel.id}
                channel={channel}
                checked={selectedChannels.includes(channel.id)}
                onToggle={() => toggleChannel(channel.id)}
              />
            ))}
          </div>
        )}
      </Card>

      {/* Media URLs */}
      <Card padding="md">
        <h3 className="text-heading-4 text-secondary mb-4">Media</h3>
        <p className="text-sm text-grey-400 mb-3">
          Add URLs to already-hosted images or videos
        </p>

        <div className="flex gap-2">
          <Input
            placeholder="https://example.com/image.jpg"
            value={newMediaUrl}
            onChange={(e) => setNewMediaUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addMediaUrl();
              }
            }}
          />
          <Button
            variant="ghost"
            onClick={addMediaUrl}
            disabled={!newMediaUrl.trim()}
            aria-label="Add media URL"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>

        {mediaUrls.length > 0 && (
          <div className="mt-3 space-y-2">
            {mediaUrls.map((url, i) => (
              <div
                key={i}
                className="flex items-center gap-2 bg-grey-50 rounded-lg px-3 py-2"
              >
                <span className="text-sm text-secondary truncate flex-1">
                  {url}
                </span>
                <button
                  onClick={() => removeMediaUrl(i)}
                  className="text-grey-400 hover:text-error shrink-0"
                  aria-label="Remove media URL"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3">
        <Button variant="ghost" onClick={() => navigate('/admin/social')}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          loading={isSaving}
          disabled={!defaultContent.trim() || selectedChannels.length === 0}
        >
          {isEditing ? 'Save Changes' : 'Add to Queue'}
        </Button>
      </div>
    </div>
  );
}

function ChannelCheckbox({
  channel,
  checked,
  onToggle,
}: {
  channel: PubChannel;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <label
      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
        checked
          ? 'border-primary bg-primary/5'
          : 'border-grey-200 hover:border-grey-300'
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="rounded border-grey-300 text-primary focus:ring-primary"
      />
      {channel.avatar && (
        <img
          src={channel.avatar}
          alt=""
          className="w-6 h-6 rounded-full object-cover"
        />
      )}
      <div className="min-w-0">
        <p className="text-sm font-medium text-secondary truncate">
          {channel.name}
        </p>
        <p className="text-xs text-grey-400">
          {NETWORK_LABELS[channel.network]}
        </p>
      </div>
    </label>
  );
}
